import React from 'react';
import type { ReadStatus } from './useStoreRead';
import type { ProductSource } from './useStoreCatalog';
import { CatalogEmpty, CatalogNote } from './catalogCards';
import { useListing } from './ListingContext';
import { goToListing, isNarrowed, withoutNarrowing } from './listingAddress';
import { useListingWords } from './listingWords';

// Why a product grid drew nothing, said to whoever can act on it: the merchant in the builder, the shopper
// on a search with no matches or a collection address the shop does not have. Anything else stays quiet.

/** What the empty grid knows: where it read from, how the read went, and whether the builder is drawing it. */
type ProductListEmptyProps = { from: ProductSource; status: ReadStatus; editing: boolean };

// Says why a page's listing is empty: the shopper's choices match nothing, which Remove all undoes, or there is nothing yet.
const ListingEmpty: React.FC = () => {
  const listing = useListing();
  const words = useListingWords();
  if (!listing || listing.read.status !== 'ready') return null;
  if (!isNarrowed(listing.address) && listing.words) return <CatalogEmpty>{words.noResults(listing.words)} {words.searchHint}</CatalogEmpty>;
  if (!isNarrowed(listing.address)) return <CatalogEmpty>{words.empty}</CatalogEmpty>;
  return (
    <CatalogEmpty>
      {words.noMatch} <button type="button" className="xe-listing-reset" onClick={() => goToListing(withoutNarrowing(listing.address))}>{words.removeAll}</button>
    </CatalogEmpty>
  );
};

// Says why a grid is empty: no match for a search, no such collection, or nothing chosen in the builder.
export const ProductListEmpty: React.FC<ProductListEmptyProps> = ({ from, status, editing }) => {
  if (from.kind === 'listing') return <ListingEmpty />;
  if (status !== 'ready' && status !== 'missing') return null;
  if (from.kind === 'search') {
    if (from.query === '') return null;
    return <CatalogEmpty>No products match “{from.query}”. Check the spelling or try a shorter word.</CatalogEmpty>;
  }
  if (from.kind === 'on-sale' && status === 'ready') return editing ? <CatalogNote>Nothing is on sale yet: give a product a compare-at price above its price.</CatalogNote> : <CatalogEmpty>Nothing is on sale right now.</CatalogEmpty>;
  if (from.kind !== 'collection') return null;
  if (status !== 'missing') return null;
  if (editing) return <CatalogNote>No collection called “{from.handle}” — pick another one.</CatalogNote>;
  return <CatalogEmpty>This collection may have been renamed or removed.</CatalogEmpty>;
};
